import React, { useState } from "react";
import TelephoneEmailLink from "./TelephoneEmailLink";
import "./Maps.css";
import { Headline, Paragraph, Section, ContentBlock } from "./ui";
import {
  GoogleMap,
  LoadScript,
  DirectionsService,
  DirectionsRenderer,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";

const customStyle = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "var(--color-primary)", // Dark background for map elements
      },
    ],
  },
  {
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#e3f1f8", // Bright, light color for text
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "var(--color-primary)", // Dark stroke for contrast
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [
      {
        color: "#4e93c2", // Subdued blue for administrative areas
      },
    ],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "var(--color-secondary)", // Softer color for country labels
      },
    ],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#e3f1f8", // Bright for localities
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [
      {
        color: "#4e93c2", // Darkened for POI background
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [
      {
        color: "#2c3a43", // Dark greenish for parks
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#4e93c2", // Highlighted roads with a medium blue
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#e3f1f8", // Light color for road labels
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [
      {
        color: "#6fa8d0", // Brighter for main roads
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#8bbedd", // Brighter shade for highways
      },
    ],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [
      {
        color: "#97d1ee", // Lightest shade for controlled access highways
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "var(--color-secondary)", // Softer for local roads
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#0a1927", // Dark water background
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#6fa8d0", // Light color for water labels
      },
    ],
  },
];

const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 50.586216,
  lng: 8.707721,
};
const customMarkerIcon = {
  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  fillColor: "#97D1EE",
  fillOpacity: 1,
  strokeWeight: 0,
  scale: 2,
};

const Maps = () => {
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [directions, setDirections] = useState(null);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");

  const handleOpenGoogleMaps = () => {

    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      "Grünberger Straße 140, 35394 Gießen"
    )}`;
    window.open(url, "_blank");
  };

  const handleMarkerClick = () => {
    setInfoWindowOpen(true);
    setInfoWindowPosition(center);
  };

  const handleDirectionsResponse = (response) => {
    if (response.status === "OK") {
      setDirections(response);
    } else {
      console.error("Error fetching directions", response);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (origin && destination) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        handleDirectionsResponse
      );
    }
  };
  return (
    <Section variant="primary" spacing="compact" margin={false}>
      <ContentBlock maxWidth="4xl" className="mx-auto mb-10">
        <div
          style={{
            display: "flex",
            borderTop: "1px solid white",
            flexWrap: "wrap",
          }}
        >
          <div className="opening-hours space-y-8">
            <div style={{ margin: "40px 0" }}>
              <Headline
                variant="h3"
                color="secondary"
                className="text-left mb-4"
              >
                Anfahrt
              </Headline>
              <Paragraph variant="base" color="secondary" className="mb-2">
                WTM Sportwagen Werkstatt
              </Paragraph>
              <Paragraph variant="base" color="secondary" className="mb-2">
                Grünberger Straße 140
              </Paragraph>
              <Paragraph variant="base" color="secondary" className="mb-4">
                35394 Gießen
              </Paragraph>
            </div>
            <div className="opening-hours-wrapper">
              <Headline
                variant="h3"
                color="secondary"
                className="text-left mb-4"
              >
                Öffnungszeiten
              </Headline>
              <div className="opening-hours__row">
                <div className="opening-hours__day">Montag - Freitag</div>
                <div className="opening-hours__time">06:30 - 17:00 Uhr</div>
              </div>
              <div className="opening-hours__row">
                <div className="opening-hours__day">Samstag</div>
                <div className="opening-hours__time">06:30 - 12:00 Uhr</div>
              </div>
              <div className="opening-hours__row">
                <div className="opening-hours__note">Und nach Vereinbarung</div>
              </div>
            </div>
            <div className="contact-wrapper">
              <Headline
                variant="h3"
                color="secondary"
                className="text-left mb-4"
              >
                Kontakt
              </Headline>

              <TelephoneEmailLink
                phoneNumber="+49 157 568 50 714"
                emailAddress="werkstatt@wtm-sportwagen.com"
              />
            </div>
          </div>

          {/* <div className="map hidden md:block">
          <LoadScript
            googleMapsApiKey="AIzaSyAAajM6qAFEx2T4stNcbQpZgE9MaNWms14"
            onError={() => console.error("Error loading Google Maps script")}
            onLoad={() => {}}
          >
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={8}
              options={{
                streetViewControl: false,
                mapTypeControl: false, // Disable the Street View control
                gestureHandling: "none",
              }}
            >
              <MarkerF title="WTM Sportwagen" position={center} />
            </GoogleMap>
          </LoadScript>
          <div 
            className="map-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(7, 18, 26, 0.25)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          ></div>
          <div
            style={{
              margin: "0 100px",
              position: "absolute",
              bottom: 0,
              width: "calc(100% - 200px",
              zIndex: 2
            }}
          >
            <button
              type="button"
              onClick={handleOpenGoogleMaps}
              style={{ margin: "40px auto", textAlign: "center" }}
              className="btn-anfahrt"
            >
              Anfahrt berechnen
            </button>
          </div>
        </div> */}
        </div>
      </ContentBlock>
    </Section>
  );
};

export default Maps;
